/**
 * `/support` — support cases queue (§11.4, §16.2): priority by prize value / claim deadline / fulfillment breach,
 * age, owner, committed next update (overdue → error tone). Filters by status, priority, category.
 */
import { useQuery } from '@tanstack/react-query';
import {
  SupportCaseStatus,
  SupportCategory,
  SupportPriority,
  type SupportCaseAdminDto,
} from '@windrop/contracts';
import { Button, Field, Select, useIntl } from '@windrop/ui';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge, ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { supportApi } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';

const PRIORITY_TONE = { URGENT: 'error', HIGH: 'warning', NORMAL: 'info', LOW: 'neutral' } as const;
const PRIORITY_RANK = { URGENT: 1, HIGH: 2, NORMAL: 3, LOW: 4 } as const;

export function SupportQueuePage() {
  const intl = useIntl();
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [category, setCategory] = useState('');
  const query = useQuery({
    queryKey: ['support', 'cases', { status, priority, category }],
    queryFn: () =>
      supportApi.list({
        ...(status ? { status: status as SupportCaseStatus } : {}),
        ...(priority ? { priority: priority as SupportPriority } : {}),
        ...(category ? { category: category as SupportCategory } : {}),
        limit: 100,
      }),
  });
  const now = Date.now();

  const columns = useMemo<Column<SupportCaseAdminDto>[]>(
    () => [
      {
        id: 'ref',
        header: 'Case',
        sortValue: (r) => r.caseRef,
        filterValue: (r) => `${r.caseRef} ${r.category} ${r.links.winId ?? ''}`,
        cell: (r) => (
          <>
            <Link to={`/support/${r.caseId}`}>
              <code>{r.caseRef}</code>
            </Link>
            <br />
            <span className="wd-muted">{humanizeStatus(r.category)}</span>
          </>
        ),
      },
      {
        id: 'priority',
        header: 'Priority',
        sortValue: (r) => PRIORITY_RANK[r.priority],
        cell: (r) => (
          <>
            <ToneBadge tone={PRIORITY_TONE[r.priority]}>{humanizeStatus(r.priority)}</ToneBadge>
            {r.escalated ? <div className="wd-muted">escalated</div> : null}
          </>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortValue: (r) => r.status,
        cell: (r) => <StatusBadge kind="SupportCaseStatus" value={r.status} />,
      },
      {
        id: 'links',
        header: 'Linked to',
        cell: (r) => (
          <>
            {r.links.winId ? (
              <Link to={`/winners/lookup/${r.links.winId}`}>
                <code>{r.links.winId}</code>
              </Link>
            ) : r.links.entryId ? (
              <span>entry {r.links.entryId.slice(-6)}</span>
            ) : (
              '—'
            )}
          </>
        ),
      },
      {
        id: 'owner',
        header: 'Owner',
        sortValue: (r) => r.owner ?? '',
        cell: (r) => (r.assignedTo ? r.owner : <span className="wd-muted">Unassigned</span>),
      },
      {
        id: 'age',
        header: 'Opened',
        sortValue: (r) => r.createdAt,
        cell: (r) => intl.formatDateTime(r.createdAt),
      },
      {
        id: 'next',
        header: 'Next update',
        sortValue: (r) => r.nextUpdateAt ?? '',
        cell: (r) =>
          r.nextUpdateAt ? (
            <span data-overdue={r.slaBreached || undefined}>
              {intl.formatDateTime(r.nextUpdateAt)}{' '}
              {r.slaBreached ? <ToneBadge tone="error">Overdue</ToneBadge> : null}
            </span>
          ) : (
            '—'
          ),
      },
    ],
    [intl],
  );

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Support cases' }]}
        title="Support cases"
        description="Priority follows prize value, claim deadline and fulfillment breach. Every case names an owner and a next update time."
      />
      <div
        style={{
          display: 'flex',
          gap: 'var(--wd-space-3)',
          flexWrap: 'wrap',
          marginBottom: 'var(--wd-space-3)',
        }}
      >
        <Field label="Status">
          <Select
            options={[
              { value: '', label: 'Any status' },
              ...SupportCaseStatus.options.map((s) => ({ value: s, label: humanizeStatus(s) })),
            ]}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          />
        </Field>
        <Field label="Priority">
          <Select
            options={[
              { value: '', label: 'Any priority' },
              ...SupportPriority.options.map((p) => ({ value: p, label: humanizeStatus(p) })),
            ]}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          />
        </Field>
        <Field label="Category">
          <Select
            options={[
              { value: '', label: 'Any category' },
              ...SupportCategory.options.map((c) => ({ value: c, label: humanizeStatus(c) })),
            ]}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
        </Field>
      </div>
      {query.isError ? (
        <ErrorState
          title="Could not load support cases"
          body="Retry in a moment."
          reference={isApiError(query.error) ? query.error.supportReference : undefined}
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : (
        <DataTable<SupportCaseAdminDto>
          caption="Support cases"
          columns={columns}
          rows={(query.data?.items ?? []).map((r) => ({
            ...r,
            slaBreached:
              r.slaBreached ||
              (!!r.nextUpdateAt &&
                Date.parse(r.nextUpdateAt) < now &&
                r.status !== 'RESOLVED' &&
                r.status !== 'CLOSED'),
          }))}
          rowKey={(r) => r.caseId}
          loading={query.isPending}
          initialSort={{ columnId: 'priority', direction: 'asc' }}
          pageSize={25}
          testId="support-table"
          emptyState={
            <EmptyState
              title="No support cases match"
              body="Change the filters or wait for participants to open one."
              action={
                <Button
                  onClick={() => {
                    setStatus('');
                    setPriority('');
                    setCategory('');
                  }}
                >
                  Clear filters
                </Button>
              }
            />
          }
          rowActions={(r) => (
            <Button variant="secondary" href={`/support/${r.caseId}`}>
              Open
            </Button>
          )}
        />
      )}
    </>
  );
}
